import React, { useEffect, useState } from "react";
import { Button, ButtonGroup, Col, Dropdown, Row, Spinner } from "react-bootstrap";
import { fetchReportInteractions } from "../../services/httpClient";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import ReportTimeInterval from "./ui/ReportTimeInterval";
import { InteractionsTotalMetric } from "./charts-metrics/InteractionsTotalMetric";
import { InteractionsBarChart } from "./charts-metrics/InteractionsBarChart";
import { InteractionsTable } from "./charts-metrics/InteractionsTable";
import ExportModal from "../ui/ExportModal";
import { InfractionsBarChart } from "./charts-metrics/InfractionsBarChart";
import { InteractionsStaleTable } from "./charts-metrics/InteractionsStaleTable";
import { activeKey, reportRunning } from "./Reports";
import { wait } from "../../helpers/loading";
import { formatDateTimeToLocal } from "../../helpers/date";

const InteractionTab = ({ buildPDF, iPad, isActive }: any) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [dateInterval, setDateInterval] = useState<number>(365);
  const [targetTypes, setTargetTypes] = useState<string[]>();
  const [visibleCount, setVisibleCount] = useState<number>(0);
  const [visibleNoteCounts, setVisibleNoteCounts] = useState<any[]>();
  const [allNoteCounts, setAllNoteCounts] = useState<any[]>([]);
  const [showAllCounts, setShowAllCounts] = useState<boolean>(false);
  const [archivedNotesExist, setArchivedNotesExist] = useState<boolean>(false);
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [interactionExportData, setInteractionExportData] = useState<any>();
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);
  const [activeCard, setActiveCard] = useState<string>("total");
  const [data, setData] = useState<any>();

  useEffect(() => {
    if (activeKey.value === "interactions") {
      reportRunning.value = false;
      const start = new Date();
      start.setDate(start.getDate() - dateInterval);
      setStartDate(start.toISOString().slice(0, 10));
      setEndDate(new Date().toISOString().slice(0, 10));
      getData(start.toISOString().slice(0, 10), new Date().toISOString().slice(0, 10));
      setVisibleCount(3);
    }
  }, [activeKey.value]);

  useEffect(() => {
    if (!allNoteCounts) return;

    const archivedNotesWithData = allNoteCounts.some((noteCount) => {
      if (noteCount?.type === "user") {
        return noteCount.is_published === 0 && noteCount.count > 0;
      } else {
        return noteCount.is_archived === 1 && noteCount.count > 0;
      }
    });
    setArchivedNotesExist(archivedNotesWithData);

    const activeNotes = allNoteCounts.filter((noteCount) => {
      const isPublished = noteCount.is_published === 1 && noteCount.is_archived !== 1;
      return isPublished;
    });

    let archivedNotes: any[] = [];
    if (showArchived) {
      archivedNotes = allNoteCounts.filter((noteCount) => {
        const isArchived = noteCount.is_archived === 1 || (noteCount.type === "user" && noteCount.is_published === 0);
        return isArchived && noteCount.count > 0;
      });
    }

    const combinedCounts = [...activeNotes, ...archivedNotes];
    const visibleNotes = showAllCounts ? combinedCounts : combinedCounts.slice(0, visibleCount);
    setVisibleNoteCounts(visibleNotes);
  }, [showAllCounts, allNoteCounts, visibleCount, showArchived]);

  const getData = async (start: string, end: string) => {
    const [notes] = await Promise.all([getInteractions(start, end)]);

    setData({
      notes,
    });
    setIsLoading(false);
  };

  const getInteractions = async (start: string, end: string) => {
    // Request the aggregated summary for fast chart rendering
    return fetchReportInteractions(start, end, true)
      .then(async (res: any) => {
        const data = res[0].data || {};
        data?.stale_cases?.forEach((stale: any) => {
          if (stale?.most_recent_date) {
            const localDateTime = formatDateTimeToLocal(stale.most_recent_date);
            stale.most_recent_date_only = localDateTime.date;
          }
        });

        const noteTypesArr = data.note_type_counts || [];
        const filteredNoteTypes = noteTypesArr?.filter((t: any) => t?.type !== "system");
        const noteTypeNames = filteredNoteTypes.map((n: any) => n.name);
        setTargetTypes(noteTypeNames);
        setAllNoteCounts(filteredNoteTypes);
        return data;
      })
      .catch((err: any) => {
        console.log(err);
        return null;
      })
      .finally(() => {
        setActiveCard("total");
      });
  };

  const onClickDownloadReport = async () => {
    reportRunning.value = true;
    setShowArchived(true);

    if (allNoteCounts?.length > visibleCount) {
      setShowAllCounts(true);
      setShowArchived(true);
    }
    await wait(1000);
    downloadReport();
  };

  const downloadReport = async () => {
    try {
      const reportSection = document.getElementById("interaction-section");
      const reportTitle = document.getElementById("interaction-title");
      const refreshButton = document.getElementById("interaction-refresh-button");
      const showArchivedButton = document.getElementById("show-archived-button");
      const showLessButton = document.getElementById("show-less-button");
      const barChart = document.getElementById("interaction-bar");

      if (!reportSection) {
        console.error("Report section not found");
        reportRunning.value = false;
        return;
      }

      if (reportTitle) {
        reportTitle.style.visibility = "hidden";
        reportTitle.style.display = "none";
      }

      if (refreshButton) refreshButton.style.visibility = "hidden";
      if (showArchivedButton) showArchivedButton.style.visibility = "hidden";
      if (showLessButton) showLessButton.style.visibility = "hidden";

      const images = reportSection.getElementsByTagName("img");
      for (let i = 0; i < images.length; i++) {
        images[i].crossOrigin = "anonymous";
      }

      await buildPDF(reportSection, "Interactions");
      reportRunning.value = false;

      if (reportTitle) {
        reportTitle.style.visibility = "visible";
        reportTitle.style.display = "flex";
      }

      if (refreshButton) refreshButton.style.visibility = "visible";
      if (showArchivedButton) showArchivedButton.style.visibility = "visible";
      if (showLessButton) showLessButton.style.visibility = "visible";

      if (barChart) {
        barChart.style.visibility = "visible";
        barChart.style.display = "flex";
      }
    } catch (err) {
      console.error("Failed to generate interaction report", err);
      reportRunning.value = false;
    }
  };

  useEffect(() => {
    if (showExportModal && startDate && endDate) {
      setIsExportLoading(true);
      // fetch full rows only for export
      fetchReportInteractions(startDate, endDate, false)
        .then((res: any) => {
          const allNotes = res[0].data || [];
          setInteractionExportData(allNotes);
        })
        .catch((err: any) => console.error(err))
        .finally(() => {
          setIsExportLoading(false);
        });
    }
  }, [showExportModal, startDate, endDate]);

  const noteTypeNames = Array.from(new Set(interactionExportData?.map((n: any) => n.name)));

  const onClickCard = (cardId: string) => {
    setActiveCard((prev) => (prev === cardId ? "total" : cardId));
  };

  if (isLoading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" style={{ color: "#F6893D" }} />
      </div>
    );
  }

  return (
    <div id="interaction-section">
      <Row id="interaction-title">
        <Col className="col-8">
          <h4 className="hero-heading app-header-text">Interactions</h4>
        </Col>
        <Col className="text-end col-4">
          {!iPad && (
            <>
              <Dropdown as={ButtonGroup}>
                <Dropdown.Toggle id="dropdown-custom-1" className="app-primary-color button">
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                  Downloads
                </Dropdown.Toggle>
                <Dropdown.Menu className="super-colors">
                  <Dropdown.Item eventKey="1" onClick={() => onClickDownloadReport()}>
                    Report
                  </Dropdown.Item>
                  <Dropdown.Item eventKey="2" onClick={() => setShowExportModal(true)}>
                    Data Export
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              <ExportModal
                showModal={showExportModal}
                setShowModal={setShowExportModal}
                interactionExportData={interactionExportData}
                noteTypes={noteTypeNames}
                startDate={startDate}
                endDate={endDate}
                isLoading={isExportLoading}
              />
            </>
          )}
        </Col>
      </Row>
      <div className="pdf-page">
        <Row>
          <Col>
            <ReportTimeInterval
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              dateInterval={dateInterval}
              setDateInterval={setDateInterval}
              intervalOne={30}
              intervalOneText={"Last Month"}
              intervalTwo={90}
              intervalTwoText={"Last 3 Months"}
              intervalThree={180}
              intervalThreeText={"Last 6 Months"}
              intervalFour={365}
              intervalFourText={"Last Year"}
              onClickRefresh={getData}
              hideBtnId="interaction-refresh-button"
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col xs={3} className="p-2">
            <InteractionsTotalMetric
              title={"All Interactions"}
              data={data}
              readOnly={false}
              active={activeCard === "total"}
              onHandleClick={() => onClickCard("total")}
            />
          </Col>
          {visibleNoteCounts?.map((n: any, index: number) => (
            <Col key={index} xs={3} className="p-2">
              <InteractionsTotalMetric
                key={n?.name}
                title={n?.name}
                data={n?.count}
                readOnly={false}
                active={activeCard === n?.name}
                onHandleClick={() => onClickCard(n?.name)}
              />
            </Col>
          ))}
          <Col xs={12} className="text-center d-flex justify-content-center gap-3">
            {allNoteCounts?.length > visibleCount && !showAllCounts && (
              <Button className="app-header-text" variant="link" onClick={() => setShowAllCounts(true)}>
                {"Show More"}
              </Button>
            )}
            {archivedNotesExist && (showAllCounts || allNoteCounts.length <= visibleCount) && (
              <Button id="show-archived-button" className="app-header-text" variant="link" onClick={() => setShowArchived((prev) => !prev)}>
                {showArchived ? "Hide Archived" : "View Archived"}
              </Button>
            )}
            {allNoteCounts?.length > visibleCount && showAllCounts && (
              <Button
                id="show-less-button"
                className="app-header-text"
                variant="link"
                onClick={() => {
                  setShowAllCounts(false);
                  setShowArchived(false);
                }}
              >
                {"Show Less"}
              </Button>
            )}
          </Col>
        </Row>
      </div>
      <div className="pdf-page">
        <Row id="interaction-bar" className="mb-5">
          <Col xs={12}>
            <InteractionsBarChart data={data} activeCard={activeCard} isActive={isActive} />
          </Col>
        </Row>
      </div>
      {targetTypes && (activeCard === "total" || activeCard === "") && (
        <Row className="mb-5">
          <Col xs={12}>
            <InteractionsTable data={data} />
          </Col>
        </Row>
      )}
      {activeCard === "Infraction" && (
        <Row className="mb-5">
          <Col xs={12}>
            <InfractionsBarChart data={data} isActive={true} />
          </Col>
        </Row>
      )}
      <div className="pdf-page">
        <Row className="mb-5">
          <Col xs={12}>
            <InteractionsStaleTable data={data} />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default InteractionTab;
